import React, { useEffect, useState, useRef } from "react";
import "../../pages/styles/styles.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInventoryList,
  deleteInventory,
  uploadInventoryCsv,
  downloadInventoryReport,
} from "../../app/redux/slice/inventory/inventorySlice";

import AddInventoryModal from "../inventory/addinventory";
import Swal from "sweetalert2";

import {
  FaUpload,
  FaDownload,
} from "react-icons/fa";

const Inventory = () => {
  const dispatch = useDispatch();

  // =====================================================
  // REDUX STATE
  // =====================================================

  const {
    data: Inventories = [],
    loading = false,
  } = useSelector(
    (state) => state.inventoryList || {}
  );

  // =====================================================
  // ROLE
  // =====================================================

  const roleId = localStorage.getItem("roleid");

  const isReadOnly =
    roleId === "2" || roleId === "3";

  // =====================================================
  // STATE
  // =====================================================

  const [showModal, setShowModal] = useState(false);

  const [selectedinventory, setSelectedinventory] =
    useState(null);

  const [searchQuery, setSearchQuery] =
    useState("");

  const [page, setPage] = useState(1);

  const [uploading, setUploading] =
    useState(false);

  const [downloading, setDownloading] =
    useState(false);

  const fileInputRef = useRef(null);

  const inventoriesPerPage = 5;

  // =====================================================
  // FETCH INVENTORY LIST
  // =====================================================

  useEffect(() => {
    dispatch(fetchInventoryList());
  }, [dispatch]);

  // =====================================================
  // ADD INVENTORY
  // =====================================================

  const handleAddinventory = () => {
    setSelectedinventory(null);
    setShowModal(true);
  };

  // =====================================================
  // EDIT INVENTORY
  // =====================================================

  const handleEdit = (inventory) => {
    setSelectedinventory(inventory);
    setShowModal(true);
  };

  // =====================================================
  // DELETE INVENTORY
  // =====================================================

  const handleDelete = (inventoryId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won’t be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteInventory(
            inventoryId,
            dispatch
          );

          // deleteInventory already refreshes
          // the inventory list
        } catch (error) {
          console.error(
            "Delete inventory error:",
            error
          );
        }
      }
    });
  };

  // =====================================================
  // MODAL SUBMIT
  // =====================================================

  const handleModalSubmit = async () => {
    setShowModal(false);
    setSelectedinventory(null);

    // Refresh latest inventory data
    dispatch(fetchInventoryList());
  };

  // =====================================================
  // UPLOAD INVENTORY BUTTON
  // =====================================================

  const handleUploadClick = () => {
    if (uploading) {
      return;
    }

    fileInputRef.current?.click();
  };

  // =====================================================
  // EXCEL FILE SELECT
  // =====================================================

const handleFileChange = async (e) => {
  const file = e.target.files?.[0];

  // Allow selecting the same file again
  e.target.value = "";

  if (!file) {
    return;
  }

  // ===================================================
  // CHECK CSV FILE
  // ===================================================

  if (
    !file.name
      .toLowerCase()
      .endsWith(".csv")
  ) {
    Swal.fire({
      title: "Invalid File",
      text: "Please select a CSV file (.csv).",
      icon: "error",
    });

    return;
  }

  // ===================================================
  // UPLOAD
  // ===================================================

  try {
    setUploading(true);

    const response =
      await uploadInventoryCsv(
        file,
        dispatch
      );

    // =================================================
    // SUCCESS
    // =================================================

    Swal.fire({
      title: "Upload Successful",
      text:
        response?.message ||
        "Inventory uploaded successfully.",
      icon: "success",
      confirmButtonText: "OK",
    });

    setPage(1);

  } catch (error) {

    console.error(
      "Inventory CSV upload error:",
      error
    );

    // =================================================
    // GET VALIDATION ERRORS
    // =================================================

    const errors =
      error?.errors ||
      error?.data?.errors ||
      [];

    console.log(
      "CSV VALIDATION ERRORS:",
      errors
    );

    // =================================================
    // SHOW VALIDATION ERRORS
    // =================================================

    if (errors.length > 0) {

      const errorHtml = errors
        .map(
          (item) => `
            <div
              style="
                text-align: left;
                padding: 10px;
                margin-bottom: 8px;
                border-bottom: 1px solid #eee;
              "
            >

              <div>
                <strong>Row:</strong>
                ${item.rowNumber ?? "-"}
              </div>

              <div>
                <strong>Column:</strong>
                ${item.columnName ?? "-"}
              </div>

              <div>
                <strong>Error:</strong>
                ${item.message ?? "Invalid value"}
              </div>

            </div>
          `
        )
        .join("");

      Swal.fire({
        title: "CSV Validation Failed",

        html: `
          <div
            style="
              max-height: 450px;
              overflow-y: auto;
              padding: 5px;
            "
          >
            ${errorHtml}
          </div>
        `,

        icon: "error",

        width: "650px",

        confirmButtonText: "OK",
      });

      return;
    }

    // =================================================
    // GENERAL ERROR
    // =================================================

    Swal.fire({
      title: "Upload Failed",

      text:
        error?.errorMsg ||
        error?.message ||
        "Unable to upload inventory CSV.",

      icon: "error",

      confirmButtonText: "OK",
    });

  } finally {
    setUploading(false);
  }
};
  // =====================================================
  // DOWNLOAD INVENTORY REPORT
  // =====================================================

  const handleDownloadReport = async () => {
    if (downloading) {
      return;
    }

    try {
      setDownloading(true);

      await downloadInventoryReport();

    } catch (error) {
      console.error(
        "Download inventory report error:",
        error
      );

      Swal.fire({
        title: "Download Failed",
        text:
          error?.errorMsg ||
          error?.message ||
          "Unable to download inventory report.",
        icon: "error",
      });
    } finally {
      setDownloading(false);
    }
  };

  // =====================================================
  // SEARCH
  // =====================================================

  const filteredInventories =
    Inventories?.filter((inventory) =>
      Object.values(inventory).some((val) =>
        String(val)
          .toLowerCase()
          .includes(
            searchQuery.toLowerCase()
          )
      )
    ) || [];

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(
    filteredInventories.length /
      inventoriesPerPage
  );

  const indexOfLast =
    page * inventoriesPerPage;

  const indexOfFirst =
    indexOfLast - inventoriesPerPage;

  const currentInventories =
    filteredInventories.slice(
      indexOfFirst,
      indexOfLast
    );

  // =====================================================
  // RESET PAGE IF SEARCH RESULT CHANGES
  // =====================================================

  useEffect(() => {
    if (
      totalPages > 0 &&
      page > totalPages
    ) {
      setPage(totalPages);
    }

    if (
      totalPages === 0 &&
      page !== 1
    ) {
      setPage(1);
    }
  }, [totalPages, page]);

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          height: "60vh",
        }}
      >
        <div
          className="spinner-border text-primary"
          role="status"
        />
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="list-container">

      {/* =================================================
          MOBILE ALIGNMENT FIXES (scoped)
          Move into styles.css if you prefer a single
          stylesheet — kept here so it travels with the
          component.
      ================================================= */}

      <style>{`
        .inv-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }
        .inv-header h4 {
          margin: 0;
        }
        .inv-btn-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        @media (max-width: 576px) {
          .inv-header {
            flex-direction: column;
            align-items: stretch;
          }
          .inv-header h4 {
            text-align: center;
            margin-bottom: 4px;
          }
          .inv-btn-group {
            justify-content: stretch;
          }
          .inv-btn-group button {
            flex: 1 1 auto;
            min-width: 0 !important;
            font-size: 0.9rem;
            padding: 8px 6px;
          }

          .list-table thead {
            display: none;
          }
          .list-table,
          .list-table tbody,
          .list-table tr,
          .list-table td {
            display: block;
            width: 100%;
          }
          .list-table tr {
            margin-bottom: 12px;
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            padding: 8px 12px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          }
          .list-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            text-align: right;
            padding: 8px 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .list-table td:last-child {
            border-bottom: none;
          }
          .list-table td::before {
            content: attr(data-label);
            font-weight: 600;
            text-align: left;
            margin-right: 10px;
            flex-shrink: 0;
          }
          .list-table td.action-buttons {
            justify-content: flex-end;
            gap: 8px;
          }
          .list-table td.action-buttons::before {
            content: "";
            margin: 0;
          }
        }
      `}</style>

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="list-header inv-header">

        <h4>
          Inventory Details
        </h4>

        {!isReadOnly && (
          <div className="inv-btn-group">

            {/* ==========================================
                HIDDEN FILE INPUT
            ========================================== */}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{
                display: "none",
              }}
              onChange={handleFileChange}
            />

            {/* ==========================================
                UPLOAD INVENTORY
            ========================================== */}

            <button
              type="button"
              onClick={handleUploadClick}
              disabled={uploading}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                minWidth: "150px",
                opacity: uploading ? 0.7 : 1,
                cursor: uploading
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              <FaUpload />

              {uploading
                ? "Uploading..."
                : "Upload CSV"}
            </button>

            {/* ==========================================
                DOWNLOAD INVENTORY
            ========================================== */}

            <button
              type="button"
              onClick={
                handleDownloadReport
              }
              disabled={downloading}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                minWidth: "150px",
                opacity: downloading
                  ? 0.7
                  : 1,
                cursor: downloading
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              <FaDownload />

              {downloading
                ? "Downloading..."
                : "Download"}
            </button>

            {/* ==========================================
                ADD INVENTORY
            ========================================== */}

            <button
              type="button"
              onClick={
                handleAddinventory
              }
              style={{
                minWidth: "150px",
              }}
            >
              + Add Inventory
            </button>

          </div>
        )}
      </div>

      {/* =================================================
          SEARCH
      ================================================= */}

      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(
            e.target.value
          );

          setPage(1);
        }}
        style={{
          padding: "0.5rem 1rem",
          marginBottom: "1rem",
          width: "100%",
          borderRadius: "6px",
          border: "1px solid #ccc",
          fontSize: "1rem",
        }}
      />

      {/* =================================================
          INVENTORY TABLE
      ================================================= */}

      <table className="list-table">

        <thead>
          <tr>

            <th>
              ID
            </th>

            <th>
              Category
            </th>

            <th>
              Sub-Category
            </th>

            <th>
              Item
            </th>

            <th>
              Veg/Non-Veg
            </th>

            {!isReadOnly && (
              <th>
                Actions
              </th>
            )}

          </tr>
        </thead>

        <tbody>

          {currentInventories.length > 0 ? (

            currentInventories.map(
              (inventory, index) => (

                <tr
                  key={inventory.id}
                >

                  {/* =================================
                      ID
                  ================================= */}

                  <td data-label="Id">
                    {indexOfFirst +
                      index +
                      1}
                  </td>

                  {/* =================================
                      CATEGORY
                  ================================= */}

                  <td
                    data-label="Category"
                  >
                    {inventory.category}
                  </td>

                  {/* =================================
                      SUB CATEGORY
                  ================================= */}

                  <td
                    data-label="Subcategory"
                  >
                    {inventory.subCategory}
                  </td>

                  {/* =================================
                      ITEM NAME
                  ================================= */}

                  <td
                    data-label="Item Name"
                  >
                    {inventory.itemName}
                  </td>

                  {/* =================================
                      VEG / NON VEG
                  ================================= */}

                  <td
                    data-label="Type"
                    style={{
                      color:
                        inventory.isVeg
                          ? "green"
                          : "red",
                      fontWeight:
                        "bold",
                    }}
                  >
                    {inventory.isVeg
                      ? "Veg"
                      : "Non-Veg"}
                  </td>

                  {/* =================================
                      ACTIONS
                  ================================= */}

                  {!isReadOnly && (

                    <td
                      data-label="Actions"
                      className="action-buttons"
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems:
                          "center",
                      }}
                    >

                      {/* EDIT */}

                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() =>
                          handleEdit(
                            inventory
                          )
                        }
                      >
                        Edit
                      </button>

                      {/* DELETE */}

                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() =>
                          handleDelete(
                            inventory.id
                          )
                        }
                      >
                        Delete
                      </button>

                    </td>
                  )}

                </tr>
              )
            )

          ) : (

            /* =========================================
               NO DATA
            ========================================= */

            <tr>

              <td
                colSpan={
                  !isReadOnly
                    ? 6
                    : 5
                }
                style={{
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                No inventories found.
              </td>

            </tr>

          )}

        </tbody>

      </table>

      {/* =================================================
          PAGINATION
      ================================================= */}

      {totalPages > 1 && (

        <div
          className="pagination-container"
        >

          <div className="pagination">

            {/* FIRST */}

            <button
              type="button"
              onClick={() =>
                setPage(1)
              }
              disabled={page === 1}
            >
              &laquo;
            </button>

            {/* PREVIOUS */}

            <button
              type="button"
              onClick={() =>
                setPage((prev) =>
                  Math.max(
                    prev - 1,
                    1
                  )
                )
              }
              disabled={page === 1}
            >
              &lt;
            </button>

            {/* PAGE NUMBER */}

            <span>
              Page{" "}
              <strong>
                {page}
              </strong>{" "}
              of{" "}
              {totalPages}
            </span>

            {/* NEXT */}

            <button
              type="button"
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    prev + 1,
                    totalPages
                  )
                )
              }
              disabled={
                page === totalPages
              }
            >
              &gt;
            </button>

            {/* LAST */}

            <button
              type="button"
              onClick={() =>
                setPage(
                  totalPages
                )
              }
              disabled={
                page === totalPages
              }
            >
              &raquo;
            </button>

          </div>

        </div>
      )}

      {/* =================================================
          ADD / EDIT INVENTORY MODAL
      ================================================= */}

      <AddInventoryModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setSelectedinventory(null);
        }}
        onSubmit={
          handleModalSubmit
        }
        inventory={
          selectedinventory
        }
      />

    </div>
  );
};

export default Inventory;